import { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { Download, Link2, FlaskConical, Flame, Snowflake, Share2, X } from "lucide-react";
import { toast } from "sonner";
import { fmtOdds, pct, SPORT_META } from "@/lib/odds";
import { ShareButtons, playerShareText } from "@/components/ShareButtons";

export function PlayerShareCard({ d, innerRef }) {
  const { player, record: r } = d;
  const meta = SPORT_META[player.sport];
  const s = r.hot || r.cold;
  return (
    <div ref={innerRef} data-testid="player-share-card" className="relative w-full max-w-md overflow-hidden rounded-2xl border border-[#26334D] bg-[#0B0E14] p-6" style={{ backgroundImage: `radial-gradient(ellipse 80% 40% at 50% -10%, ${meta?.color || "#10B981"}40, transparent)` }}>
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 text-emerald-400"><FlaskConical size={16} /><span className="font-display text-lg font-black uppercase tracking-tight text-white">The Parlay <span className="text-emerald-400">Lab</span></span></span>
        <span className="rounded-full border border-[#26334D] px-3 py-0.5 font-mono-num text-[10px] font-bold uppercase tracking-widest text-slate-300">{meta?.short} · {player.team}</span>
      </div>
      <p className="mt-5 text-xs uppercase tracking-widest text-slate-500">Model record · last {d.window_days || 21} days</p>
      <h1 className="font-display mt-1 text-4xl font-black uppercase leading-none tracking-tight text-white" data-testid="share-card-name">{player.name}</h1>
      {s && (
        <p data-testid="share-card-streak" className={`mt-3 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-wider ${r.hot ? "border-orange-500/50 bg-orange-500/10 text-orange-300" : "border-sky-500/50 bg-sky-500/10 text-sky-300"}`}>
          {r.hot ? <Flame size={12} /> : <Snowflake size={12} />} {s.streak} straight {r.hot ? "hits" : "misses"} · {s.stat}
        </p>
      )}
      <div className="mt-5 grid grid-cols-3 gap-2">
        {[["Hit rate", r.hit_rate != null ? `${r.hit_rate}%` : "—", r.hit_rate >= 55 ? "text-emerald-400" : r.hit_rate < 45 ? "text-red-400" : "text-amber-400"], ["Record", `${r.won}–${r.lost}`, "text-white"], ["Calls", r.n, "text-slate-200"]].map(([l, v, c]) => (
          <div key={l} className="rounded-lg bg-[#121824] px-3 py-2"><p className="text-[10px] uppercase tracking-wider text-slate-500">{l}</p><p className={`font-mono-num text-xl font-bold ${c}`}>{v}</p></div>
        ))}
      </div>
      {d.last_results?.length > 0 && (
        <div className="mt-4">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Last {d.last_results.length} calls</p>
          <div className="mt-1.5 flex gap-1.5" data-testid="share-card-dots">{d.last_results.map((res, i) => <span key={i} className={`h-3 w-6 rounded-sm ${res === "won" ? "bg-emerald-400" : "bg-red-500/80"}`} />)}</div>
        </div>
      )}
      {d.model_call && (
        <div className="mt-4 flex items-center gap-3 rounded-lg border border-[#26334D] bg-[#121824] px-3 py-2">
          <span className="min-w-0 flex-1"><p className="text-[10px] uppercase tracking-wider text-slate-500">Model's call today{d.today ? ` · ${d.today.matchup}` : ""}</p><p className="truncate text-sm font-semibold text-white">{d.model_call.selection.replace(/^.*?\(\w+\) /, "")}</p></span>
          <span className="font-mono-num text-sm font-bold text-amber-400">{fmtOdds(d.model_call.odds)}</span>
          <span className="font-mono-num text-xs text-emerald-400">{pct(d.model_call.win_probability)}</span>
        </div>
      )}
      <p className="mt-4 text-[10px] uppercase tracking-widest text-slate-600">theparlaylab · AI probability picks · results simulated</p>
    </div>
  );
}

export function usePlayerCardActions(ref, slug) {
  const download = async () => {
    try {
      const url = await toPng(ref.current, { pixelRatio: 2, cacheBust: true });
      const a = document.createElement("a");
      a.href = url;
      a.download = `parlay-lab-${slug}.png`;
      a.click();
      toast.success("Player card downloaded");
    } catch (e) {
      toast.error("Could not render image");
    }
  };
  const copy = async () => {
    await navigator.clipboard.writeText(`${window.location.origin}/p/${slug}`);
    toast.success("Public link copied");
  };
  return { download, copy };
}

export function ShareCardModal({ d }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const cardData = { player: d.player, record: d.record, window_days: d.window_days, today: d.today, last_results: d.record.last_calls.filter((c) => c.result !== "pending").slice(0, 10).map((c) => c.result), model_call: d.calls_today[0] || null };
  const { download, copy } = usePlayerCardActions(ref, d.player.slug);
  return (
    <>
      <button data-testid="share-card-btn" onClick={() => setOpen(true)} className="btn-ghost !px-4 !py-2 text-xs"><Share2 size={14} /> Share card</button>
      {open && (
        <div data-testid="share-card-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div className="fade-up flex w-full max-w-md flex-col items-center gap-4" onClick={(e) => e.stopPropagation()}>
            <PlayerShareCard d={cardData} innerRef={ref} />
            <div className="flex flex-wrap justify-center gap-3">
              <button data-testid="share-card-download-btn" onClick={download} className="btn-primary"><Download size={16} /> Download PNG</button>
              <button data-testid="share-card-copy-link-btn" onClick={copy} className="btn-ghost"><Link2 size={16} /> Copy public link</button>
              <button data-testid="share-card-close-btn" onClick={() => setOpen(false)} className="btn-ghost !px-3"><X size={16} /></button>
            </div>
            <ShareButtons text={playerShareText(cardData)} url={`${window.location.origin}/p/${d.player.slug}`} testPrefix="player-share" />
          </div>
        </div>
      )}
    </>
  );
}
